import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../services/api';
import './TestPublish.css';

const STATUS_LABELS = {
  approved: { label: 'Approved', className: 'status-approved' },
  queued: { label: 'Queued', className: 'status-queued' },
  failed: { label: 'Failed', className: 'status-failed' },
  video_failed: { label: 'Video Failed', className: 'status-failed' },
  publishing: { label: 'Publishing…', className: 'status-publishing' },
  published: { label: 'Published ✓', className: 'status-published' },
  partially_published: { label: 'Partial ⚠', className: 'status-partial' },
};

const PLATFORM_RESULT_ICONS = {
  published: '✅',
  account_inactive: '⛔',
  account_not_connected: '🔌',
  page_not_connected: '🔌',
  token_expired: '🔑',
  token_invalid: '🔑',
};

const getPlatformResultIcon = (result) => {
  if (!result) return '—';
  if (PLATFORM_RESULT_ICONS[result]) return PLATFORM_RESULT_ICONS[result];
  if (result.startsWith('failed:')) return '❌';
  if (result.startsWith('token_expired:')) return '🔑';
  return '❓';
};

const isRetryable = (status) =>
  ['failed', 'video_failed', 'partially_published'].includes(status);

function TestPublish() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState('publish'); // 'publish' | 'retry'
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  const fetchPosts = useCallback(async () => {
    try {
      setError(null);
      const response = await apiClient.get('/api/v1/admin/test-publish/posts');
      setPosts(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('testPublish.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handlePublishClick = (post) => {
    setSelectedPost(post);
    setConfirmAction('publish');
    setSelectedPlatforms([...(post.target_platforms || ['instagram'])]);
    setShowConfirm(true);
    setResult(null);
  };

  const handleRetryClick = (post) => {
    setSelectedPost(post);
    setConfirmAction('retry');
    setSelectedPlatforms([...(post.target_platforms || ['instagram'])]);
    setShowConfirm(true);
    setResult(null);
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  /** Quick-publish to a single platform (opens confirm with only that platform). */
  const handleQuickPlatformPublish = (post, platform) => {
    setSelectedPost(post);
    setConfirmAction(isRetryable(post.status) ? 'retry' : 'publish');
    setSelectedPlatforms([platform]);
    setShowConfirm(true);
    setResult(null);
  };

  /** Republish on ALL active platforms the user has connected. */
  const handleRepublishAll = (post) => {
    const all = [];
    if (post.instagram_account?.is_active && post.instagram_account?.is_token_valid) all.push('instagram');
    if (post.tiktok_account?.is_active && (post.tiktok_account?.is_token_valid || post.tiktok_account?.is_business_token_valid)) all.push('tiktok');
    if (post.facebook_page?.is_active && post.facebook_page?.is_token_valid) all.push('facebook');
    setSelectedPost(post);
    setConfirmAction(isRetryable(post.status) ? 'retry' : 'publish');
    setSelectedPlatforms(all);
    setShowConfirm(true);
    setResult(null);
  };

  const handleConfirmPublish = async () => {
    if (!selectedPost) return;

    setShowConfirm(false);
    setPublishing(selectedPost.id);
    setResult(null);

    try {
      const response = await apiClient.post('/api/v1/admin/test-publish/trigger', {
        post_id: selectedPost.id,
        platforms: selectedPlatforms.length > 0 ? selectedPlatforms : undefined,
      });
      setResult({
        type: 'success',
        message: response.data.message,
        postId: selectedPost.id,
      });
      // Refresh list after short delay to see status change
      setTimeout(fetchPosts, 3000);
    } catch (err) {
      setResult({
        type: 'error',
        message: err.response?.data?.detail || t('testPublish.publishError'),
        postId: selectedPost.id,
      });
    } finally {
      setPublishing(null);
    }
  };

  const getStatusBadge = (status) => {
    const info = STATUS_LABELS[status] || { label: status, className: 'status-default' };
    return <span className={`status-badge ${info.className}`}>{info.label}</span>;
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="test-publish-page">
      <div className="page-header">
        <div>
          <h1>🧪 {t('testPublish.title')}</h1>
          <p className="page-subtitle">{t('testPublish.subtitle')}</p>
        </div>
        <button className="btn btn-secondary" onClick={fetchPosts}>
          🔄 {t('testPublish.refresh')}
        </button>
      </div>

      {/* Warning banner */}
      <div className="warning-banner">
        <span className="warning-icon">⚠️</span>
        <div>
          <strong>{t('testPublish.warningTitle')}</strong>
          <p>{t('testPublish.warningText')}</p>
        </div>
      </div>

      {/* Result toast */}
      {result && (
        <div className={`result-toast ${result.type}`}>
          {result.type === 'success' ? '✅' : '❌'} {result.message}
        </div>
      )}

      {error && (
        <div className="result-toast error">❌ {error}</div>
      )}

      {/* Posts table */}
      {posts.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📭</span>
          <h3>{t('testPublish.noPosts')}</h3>
          <p>{t('testPublish.noPostsHint')}</p>
        </div>
      ) : (
        <div className="posts-table-container">
          <table className="posts-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('testPublish.user')}</th>
                <th>{t('testPublish.caption')}</th>
                <th>{t('testPublish.platforms')}</th>
                <th>{t('testPublish.status')}</th>
                <th>{t('testPublish.video')}</th>
                <th>{t('testPublish.action')}</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => {
                const isPublishing = publishing === post.id;
                const platforms = post.target_platforms || ['instagram'];
                const igOk = post.instagram_account?.is_active && post.instagram_account?.is_token_valid;
                const ttOk = post.tiktok_account?.is_active && (post.tiktok_account?.is_token_valid || post.tiktok_account?.is_business_token_valid);
                const fbOk = post.facebook_page?.is_active && post.facebook_page?.is_token_valid;
                const hasAnyAccount = (platforms.includes('instagram') && igOk)
                  || (platforms.includes('tiktok') && ttOk)
                  || (platforms.includes('facebook') && fbOk);
                const canPublish = hasAnyAccount && !isPublishing && post.status !== 'publishing';
                const canRetry = isRetryable(post.status) && hasAnyAccount && !isPublishing;

                return (
                  <tr key={post.id} className={isPublishing ? 'row-publishing' : ''}>
                    <td className="cell-id">#{post.id}</td>
                    <td className="cell-user">
                      <div className="user-info">
                        <span className="user-name">{post.user_name || post.user_email}</span>
                        <span className="user-email">{post.user_email}</span>
                      </div>
                    </td>
                    <td className="cell-caption">
                      <span className="caption-text">{post.caption || '—'}</span>
                      {post.hashtags?.length > 0 && (
                        <span className="hashtag-count">#{post.hashtags.length} tags</span>
                      )}
                    </td>
                    <td className="cell-platforms">
                      <div className="platform-badges">
                        {platforms.includes('instagram') && (
                          <span className={`platform-badge ${igOk ? 'platform-ok' : 'platform-error'}`} title={
                            post.instagram_account
                              ? `@${post.instagram_account.username} — ${igOk ? 'OK' : t('testPublish.igTokenInvalid')}`
                              : t('testPublish.noAccount')
                          }>
                            📸 {post.platform_results?.instagram
                              ? getPlatformResultIcon(post.platform_results.instagram)
                              : ''}
                          </span>
                        )}
                        {platforms.includes('tiktok') && (
                          <span className={`platform-badge ${ttOk ? 'platform-ok' : 'platform-error'}`} title={
                            post.tiktok_account
                              ? `${post.tiktok_account.display_name} — ${ttOk ? 'OK' : t('testPublish.tokenInvalid')}`
                              : t('testPublish.noAccount')
                          }>
                            🎵 {post.platform_results?.tiktok
                              ? getPlatformResultIcon(post.platform_results.tiktok)
                              : ''}
                          </span>
                        )}
                        {platforms.includes('facebook') && (
                          <span className={`platform-badge ${fbOk ? 'platform-ok' : 'platform-error'}`} title={
                            post.facebook_page
                              ? `${post.facebook_page.page_name} — ${fbOk ? 'OK' : t('testPublish.tokenInvalid')}`
                              : t('testPublish.noAccount')
                          }>
                            📘 {post.platform_results?.facebook
                              ? getPlatformResultIcon(post.platform_results.facebook)
                              : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="cell-status">
                      {getStatusBadge(post.status)}
                      {post.status_message && (
                        <span className="status-message" title={post.status_message}>
                          ℹ️
                        </span>
                      )}
                    </td>
                    <td className="cell-video">
                      {post.video_url ? (
                        <a
                          href={post.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="video-link"
                          title={t('testPublish.viewVideo')}
                        >
                          🎬
                        </a>
                      ) : (
                        <span className="no-video">—</span>
                      )}
                    </td>
                    <td className="cell-action">
                      <div className="action-buttons">
                        {/* Main publish / retry button */}
                        {canRetry ? (
                          <button
                            className="btn btn-retry"
                            onClick={() => handleRetryClick(post)}
                            disabled={isPublishing}
                            title={t('testPublish.retryNow')}
                          >
                            {isPublishing ? <span className="spinner-inline" /> : '🔄'}
                            {isPublishing ? t('testPublish.publishing') : t('testPublish.retryBtn')}
                          </button>
                        ) : (
                          <button
                            className="btn btn-publish"
                            onClick={() => handlePublishClick(post)}
                            disabled={!canPublish || post.status === 'published'}
                            title={
                              !hasAnyAccount
                                ? t('testPublish.noActiveAccounts')
                                : post.status === 'published'
                                ? t('testPublish.alreadyPublished')
                                : t('testPublish.publishNow')
                            }
                          >
                            {isPublishing ? <span className="spinner-inline" /> : '🚀'}
                            {isPublishing ? t('testPublish.publishing') : t('testPublish.publishBtn')}
                          </button>
                        )}
                      </div>

                      {/* Quick per-platform buttons */}
                      {!isPublishing && post.status !== 'publishing' && (
                        <div className="quick-platform-actions">
                          {ttOk && (
                            <button
                              className="btn-quick-platform btn-quick-tiktok"
                              onClick={() => handleQuickPlatformPublish(post, 'tiktok')}
                              title={t('testPublish.quickTiktok')}
                            >
                              🎵 TikTok
                            </button>
                          )}
                          {igOk && (
                            <button
                              className="btn-quick-platform btn-quick-instagram"
                              onClick={() => handleQuickPlatformPublish(post, 'instagram')}
                              title={t('testPublish.quickInstagram')}
                            >
                              📸 IG
                            </button>
                          )}
                          {fbOk && (
                            <button
                              className="btn-quick-platform btn-quick-facebook"
                              onClick={() => handleQuickPlatformPublish(post, 'facebook')}
                              title={t('testPublish.quickFacebook')}
                            >
                              📘 FB
                            </button>
                          )}
                          {/* Republish All — shown when ≥ 2 active accounts */}
                          {[igOk, ttOk, fbOk].filter(Boolean).length >= 2 && (
                            <button
                              className="btn-quick-platform btn-quick-all"
                              onClick={() => handleRepublishAll(post)}
                              title={t('testPublish.republishAll')}
                            >
                              🌐 {t('testPublish.allPlatforms')}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirm && selectedPost && (
        <div className="modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>
              {confirmAction === 'retry'
                ? t('testPublish.retryTitle')
                : t('testPublish.confirmTitle')}
            </h2>
            <div className="confirm-details">
              <p>
                <strong>Post #{selectedPost.id}</strong> — {selectedPost.user_name || selectedPost.user_email}
              </p>
              <p className="confirm-caption">
                "{(selectedPost.caption || '').substring(0, 100)}…"
              </p>
              <div className="confirm-platforms">
                <strong>{t('testPublish.selectPlatforms')}:</strong>
                <div className="confirm-platform-list">
                  {(selectedPost.target_platforms || ['instagram']).map((p) => {
                    const isChecked = selectedPlatforms.includes(p);
                    const icon = p === 'instagram' ? '📸' : p === 'tiktok' ? '🎵' : '📘';
                    const accountOk = p === 'instagram'
                      ? selectedPost.instagram_account?.is_active && selectedPost.instagram_account?.is_token_valid
                      : p === 'tiktok'
                        ? selectedPost.tiktok_account?.is_active && (selectedPost.tiktok_account?.is_token_valid || selectedPost.tiktok_account?.is_business_token_valid)
                        : selectedPost.facebook_page?.is_active && selectedPost.facebook_page?.is_token_valid;
                    return (
                      <label
                        key={p}
                        className={`confirm-platform-toggle ${isChecked ? 'toggle-active' : 'toggle-inactive'} ${!accountOk ? 'toggle-disabled' : ''}`}
                        title={!accountOk ? t('testPublish.tokenInvalid') : p}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => togglePlatform(p)}
                          disabled={!accountOk}
                        />
                        {icon} {p}
                      </label>
                    );
                  })}
                </div>
              </div>
              {confirmAction === 'retry' && selectedPost.status_message && (
                <div className="confirm-error-detail">
                  <strong>{t('testPublish.previousError')}:</strong>
                  <p className="error-message-text">{selectedPost.status_message}</p>
                </div>
              )}
              {confirmAction === 'retry' && selectedPost.platform_results && (
                <div className="confirm-platform-results">
                  <strong>{t('testPublish.platformResults')}:</strong>
                  {Object.entries(selectedPost.platform_results).map(([platform, resultVal]) => (
                    <div key={platform} className={`platform-result-row ${resultVal === 'published' ? 'result-ok' : 'result-fail'}`}>
                      <span>{platform}</span>
                      <span>{getPlatformResultIcon(resultVal)} {resultVal}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="confirm-warning">
              {confirmAction === 'retry'
                ? t('testPublish.retryWarning')
                : t('testPublish.confirmWarning')}
            </p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowConfirm(false)}>
                {t('testPublish.cancel')}
              </button>
              <button
                className={`btn ${confirmAction === 'retry' ? 'btn-retry-confirm' : 'btn-danger'}`}
                onClick={handleConfirmPublish}
                disabled={selectedPlatforms.length === 0}
              >
                {confirmAction === 'retry'
                  ? `🔄 ${t('testPublish.confirmRetry')}`
                  : `🚀 ${t('testPublish.confirmPublish')}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TestPublish;
